import _ from 'lodash'
import type { Component, ViewMode } from '@wix/thunderbolt-becky-types'
import { pointers } from '@wix/thunderbolt-becky-types'
import { getUniqueId } from '@wix/thunderbolt-commons'
import type { RawModel } from '../../types'
import { SITE_NAMESPACE, SYSTEM_STYLES } from '../constants'

const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES

const DATA_QUERIES_TO_INFLATE = {
	[PROPERTY_TYPES.BEHAVIOR_QUERY]: true,
	[PROPERTY_TYPES.BREAKPOINTS_QUERY]: true,
	[PROPERTY_TYPES.PROPERTY_QUERY]: true,
	[PROPERTY_TYPES.CONNECTION_QUERY]: true,
	[PROPERTY_TYPES.DESIGN_QUERY]: true,
	[PROPERTY_TYPES.DATA_QUERY]: true,
	[PROPERTY_TYPES.LAYOUT_QUERY]: true,
	[PROPERTY_TYPES.MOBILE_HINTS_QUERY]: true,
	[PROPERTY_TYPES.STYLE_ID]: true,
	[PROPERTY_TYPES.TRANSFORMATION_QUERY]: true,
	[PROPERTY_TYPES.TRANSITION_QUERY]: true,
	[PROPERTY_TYPES.PRESETS_QUERY]: true,
	[PROPERTY_TYPES.SLOTS_QUERY]: true,
	[PROPERTY_TYPES.VARIABLES_QUERY]: true,
}

const isMobile = (viewMode: ViewMode) => viewMode === 'mobile'

const getUniqueDataQuery = (id: string, suffix: string, withMobileSuffix?: boolean) =>
	`${id}-${suffix}${withMobileSuffix ? '-mobile' : ''}`

const inflateQueries = (
	rawModel: RawModel,
	templateComp: Component,
	inflatedCompId: string,
	isMobileViewMode: boolean
) => {
	const isSystemStyle = templateComp.styleId && _.get(rawModel, [SITE_NAMESPACE, SYSTEM_STYLES, templateComp.styleId])

	return _.mapValues(DATA_QUERIES_TO_INFLATE, (__, dataQueryName) => {
		// if templateComp style points to a system style in masterPage, styleId data query should remain unchanged
		if (dataQueryName === PROPERTY_TYPES.STYLE_ID && isSystemStyle) {
			return templateComp[dataQueryName]
		}

		const isMobileProperty = isMobileViewMode && dataQueryName === PROPERTY_TYPES.PROPERTY_QUERY
		return getUniqueDataQuery(inflatedCompId, dataQueryName, isMobileProperty)
	})
}

export const inflateCompStructure = ({
	pageId,
	rawModel,
	templateComp,
	ancestorsChain,
	isRootComp,
	viewMode,
}: {
	rawModel: RawModel
	templateComp: Component
	ancestorsChain: string
	pageId: string
	isRootComp: boolean
	viewMode: ViewMode
}): Component => {
	const templateCompId = templateComp.id
	const inflatedCompId = getUniqueId(ancestorsChain, templateCompId)
	const isMobileViewMode = isMobile(viewMode)

	return {
		...templateComp,
		...inflateQueries(rawModel, templateComp, inflatedCompId, isMobileViewMode),
		templateId: templateCompId,
		id: inflatedCompId,
		metaData: {
			...templateComp.metaData,
			pageId,
		},
		parent: isRootComp ? ancestorsChain : getUniqueId(ancestorsChain, templateComp.parent as string),
		components: templateComp.components
			? templateComp.components.map((childCompId) => getUniqueId(ancestorsChain, childCompId))
			: undefined,
	}
}
